/**
 * vet-visit-row.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import RecordMenuContext from 'contexts/record-context-menu/record-menu.context';
import Icon from 'components/icon';
import { 
  shrinkOptionalWidth
} from '../helpers';
import Row from '../row/row';
import RowStatusChip from '../row/row-status-chip';


const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.grey.colors[5],
  },
  completionStatusChip: {
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      display: 'none'
    }
  },
  completionStatusIcon: {
    display: 'none',
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      display: 'flex',
      alignItems: 'center'
    }
  },
  completionChip: {
    minWidth: 110,
  },
}));


const VetVisitRow = React.memo(function (props) {
  const { 
    id, 
    basePath,
    isSelected,
    onSelectionChange,
    onViewReport,
  } = props;

  const classes = useStyles();
  const recordStore = useRecordStore();
  const vetVisitRecord = recordStore.useRecord(Types.Record.VET_VISIT, id).ornull;
  const { user } = React.useContext(UserContext);
  const { t } = useTranslation('general');
  const vetVisit = vetVisitRecord?.get();
  const eventDate = parseISO(vetVisit?.date);
  const now = new Date();
  const isFuture = eventDate > now; 
  const recordContextMenu = React.useContext(RecordMenuContext);

    if (!vetVisitRecord) return null;

  // route link
  const to = makeLocation(
    basePath,
    'vetVisit',
    'view',
    { vetVisitId: id }
  );
  const routeLink = <RouterLink to={to} className='absolute-fill record-link' />;

  // completion status
  // (ignoring isComplete here because it's entirely based on future/past)
  const completionStatusId = isFuture ? 'upcoming' : 'complete';
  const completionStatusLabel = <>
    <Icon type={completionStatusId} />
    {isFuture ? 'Upcoming' : "Complete"}
  </>


  // menu items
  const menuItems = [
    // view
    {
      title: 'View',
      iconType: 'view',
      component: RouterLink,
      to: makeLocation(basePath, 'vetVisit', 'view', { vetVisitId: id }, null),
      isDefaultItem: true,
    },

    // edit
    !user.role.isSupervisor ? {
      title: 'Edit',
      iconType: 'edit',
      component: RouterLink,
      to: makeLocation(basePath, 'vetVisit', 'edit', { vetVisitId: id }, null),
      disabled: !user.role.status.isActive,
      disabledReason: t('readOnlyAccount') 
    } : {},

    // view report
    {
      title: 'View Report',
      iconType: 'report',
      onSelect: () => onViewReport(Types.RecordGroup.VET_VISIT, [id]),
    },

    // delete
    !user.role.isSupervisor ? {
      title: 'Delete ...',
      iconType: 'delete',
      onSelect: () => recordContextMenu.deleteVetVisit(id),
      disabled: !user.role.status.isActive,
      disabledReason: t('readOnlyAccount') 
    } : {},
  ];

  return (
    <>
      {vetVisit &&
      <Row
        data-testid={id}
        data-testtype='record-vet-visit'
        recordType={Types.Record.VET_VISIT}
        className='content'
        routeLink={routeLink}

        selection={{
          type: Types.RecordGroup.VET_VISIT,
          id,
          isSelected,
          isDisabled: false, //isReportDisabled,
          disabledReason: null, //reportDisabledReason,
          onChange: onSelectionChange
        }}

        icon={<Icon type="vet-care-type" />}
        title={vetVisit.name}
        detail={vetVisit.careTypes.map(type => type.customOtherName || type.name).join(', ')}

        optional={
          <div className={`flex-hc record-status`}>
            {/* completion chip - desktop */}
            <span className='status-desktop'>
              <RowStatusChip
                className={`chip-${completionStatusId} chip-font-${completionStatusId} ${classes.completionChip}`}
                label={completionStatusLabel}
              />
            </span>
            {/* completion icon - mobile */}
            <span className='status-mobile'>
              <Icon 
                type={completionStatusId} 
                className={`icon-${completionStatusId}`} 
              />
            </span>
          </div>}

          menu={{
            ariaLabel: 'vet visit record options',
            className: '',
            items: menuItems
          }}
      />}
    </>
  )
}, (prev, next) => {
  return (
    prev.id === next.id
    && prev.isSelected === next.isSelected
    && prev.isSelectDisabled === next.isSelectDisabled
  );
});

export default VetVisitRow;